import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button component following UI design specifications
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Button component
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}) => {
  // Base styles
  const baseStyles = 'font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  // Variant styles
  const variantStyles = {
    primary: `
      bg-gradient-to-r from-emerald-500 to-emerald-600 text-white 
      hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl hover:scale-[1.02]
      focus:ring-emerald-500 shadow-lg
      disabled:hover:scale-100 disabled:hover:shadow-lg
    `,
    secondary: `
      bg-white text-emerald-600 border border-emerald-200 
      hover:bg-emerald-50 hover:shadow-lg
      focus:ring-emerald-500 shadow-md
    `,
    ghost: `
      bg-transparent text-gray-600 
      hover:bg-gray-50
      focus:ring-gray-500
    `,
    danger: `
      bg-gradient-to-r from-red-500 to-red-600 text-white 
      hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:scale-[1.02]
      focus:ring-red-500 shadow-lg
      disabled:hover:scale-100 disabled:hover:shadow-lg
    `
  };

  // Size styles
  const sizeStyles = {
    small: 'px-3 py-1.5 text-xs rounded-lg',
    medium: 'px-6 py-3 text-sm rounded-xl',
    large: 'px-8 py-4 text-base rounded-xl'
  };

  // Combine classes
  const combinedClasses = [
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    className
  ].join(' ').replace(/\s+/g, ' ').trim();

  return (
    <button
      type={type}
      className={combinedClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      <div className="flex items-center justify-center">
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {children}
      </div>
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};

export default Button; 